import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton, Tooltip,
  Autocomplete, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Snackbar, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactoryIcon from '@mui/icons-material/Factory';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
const ProductionModal = ({
  open,
  onClose,
  onSave,
  mode = 'create',
  recordData = null,
  items = [],
  allItems = []
}) => {
  const { companyId, branchId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    ItemId: '',
    ItemName: '',
    VoucherNumber: '',
    FinYear: '',
    VoucherDate: new Date().toISOString().split('T')[0],
    Narration: ''
  });

  const [productionDetails, setProductionDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [totalQuantity, setTotalQuantity] = useState(1);

  // Add new item state
  const [newItemSelection, setNewItemSelection] = useState(null);
  const [newItemQty, setNewItemQty] = useState('');
  const [newItemRate, setNewItemRate] = useState('');
  const [addItemKey, setAddItemKey] = useState(0);

  // Snackbar for stock errors
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  // Get only product items (IsProduct === true)
  const productItems = items.filter(item => item.IsProduct);

  // Get non-product items for the add row (exclude already added items)
  const availableItems = allItems.filter(item => 
    !item.IsProduct && 
    !productionDetails.some(d => d.ItemId === item.Id)
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (recordData && mode !== 'create') {
        setFormData({
          Id: recordData.Id || '',
          ItemId: recordData.ItemId || '',
          ItemName: recordData.ItemName || '',
          VoucherNumber: recordData.VoucherNumber || '',
          FinYear: recordData.FinYear || '',
          VoucherDate: recordData.VoucherDate ? recordData.VoucherDate.split('T')[0] : new Date().toISOString().split('T')[0],
          Narration: recordData.Narration || ''
        });
        // Load details from record (API response)
        const details = recordData.ProductionDetails || [];
        setProductionDetails(details.map(d => {
          const matchedItem = allItems.find(i => i.Id === d.ItemId);
          return {
            ...d,
            ItemName: d.ItemName || matchedItem?.Name || '',
            ItemCode: d.ItemCode || matchedItem?.Code || '',
            UnitQty: d.Quantity || 0,
            BalanceStock: parseFloat(matchedItem?.BalanceStock) || 0
          };
        }));
        setTotalQuantity(recordData.Quantity || '');
      } else {
        setFormData({
          ItemId: '',
          ItemName: '',
          VoucherNumber: '',
          FinYear: '',
          VoucherDate: new Date().toISOString().split('T')[0],
          Narration: ''
        });
        setProductionDetails([]);
        setTotalQuantity(1);
      }
      setErrors({});

      // Fetch max voucher number for create mode
      if (mode === 'create') {
        fetchMaxVoucherNo();
      }
    }
  }, [open, recordData, mode]);

  const fetchMaxVoucherNo = async () => {
    try {
      const response = await fetchData('ProductionApi', `/MaxVoucherNo/${companyId}/${branchId}`, token);
      const data = response?.response_data || response || {};
      setFormData(prev => ({
        ...prev,
        VoucherNumber: data.BillNumber || data.billNumber || 1,
        FinYear: data.FinYear || data.finYear || ''
      }));
    } catch (error) {
      console.error('Error fetching max voucher number:', error);
    }
  };

  // When product is selected, fetch its production relations (child items)
  const handleProductChange = async (newProduct) => {
    if (!newProduct) {
      setFormData(prev => ({ ...prev, ItemId: '', ItemName: '' }));
      setProductionDetails([]);
      return;
    }

    setFormData(prev => ({
      ...prev,
      ItemId: newProduct.Id,
      ItemName: newProduct.Code ? `${newProduct.Code} - ${newProduct.Name}` : newProduct.Name
    }));

    // Fetch production relations for this product
    setDetailsLoading(true);
    try {
      const response = await fetchData('ProductionApi', `/ProductionRelations/${companyId}/${branchId}/${newProduct.Id}`, token);
      const data = response?.response_data || response;
      const relations = data;

      const qty = parseFloat(totalQuantity) || 1;
      const details = relations.map(rel => {
        const matchedItem = allItems.find(i => i.Id === rel.ItemId);
        return {
          ItemId: rel.ItemId,
          ItemName: rel.ItemName || matchedItem?.Name || '',          
          Quantity: (rel.Quantity || 0) * qty,
          Rate: rel.Rate || 0,
          Amount: ((rel.Quantity || 0) * qty) * (rel.Rate || 0),
          UnitQty: rel.Quantity || 0,
          BalanceStock: parseFloat(rel.BalanceStock) || 0,
          IsActive: true
        };
      });
      setProductionDetails(details);
    } catch (error) {
      console.error('Error fetching production relations:', error);
      setProductionDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  // When total product quantity changes, recalculate all detail quantities
  const handleTotalQuantityChange = (e) => {
    const newQty = e.target.value;
    setTotalQuantity(newQty);

    const qty = parseFloat(newQty) || 0;
    setProductionDetails(prev => {
      const updated = prev.map(d => {
        const newDetailQty = (d.UnitQty || 0) * qty;
        return {
          ...d,
          Quantity: newDetailQty,
          Amount: newDetailQty * (d.Rate || 0)
        };
      });

      // Stock validation after recalculation
      const stockErrors = getStockErrors(updated);
      if (stockErrors.length > 0) {
        showError(stockErrors.join(' | '));
      }

      return updated;
    });

    if (errors.TotalQuantity) {
      setErrors(prev => ({ ...prev, TotalQuantity: '' }));
    }
  };

  // Handle individual detail field changes
  const handleDetailChange = (index, field, value) => {
    setProductionDetails(prev => {
      const updated = prev.map((d, i) => {
        if (i !== index) return d;
        const updatedRow = { ...d, [field]: parseFloat(value) || 0 };
        if (field === 'Quantity' || field === 'Rate') {
          updatedRow.Amount = (updatedRow.Quantity || 0) * (updatedRow.Rate || 0);
        }
        return updatedRow;
      });

      // Stock validation when quantity changes
      if (field === 'Quantity') {
        const detail = updated[index];
        const numValue = parseFloat(value) || 0;
        const itemData = allItems.find(i => i.Id === parseInt(detail.ItemId));
        const stock = detail.BalanceStock !== undefined
          ? parseFloat(detail.BalanceStock) || 0
          : parseFloat(itemData?.BalanceStock) || 0;
        if (numValue > stock) {
          showError(`Quantity (${numValue}) exceeds available stock (${stock}) for ${detail.ItemName || itemData?.Name || 'this item'}`);
        }
      }

      return updated;
    });
  };

  // Add new item to details
  const handleAddNewItem = () => {
    if (!newItemSelection || !newItemQty || parseFloat(newItemQty) <= 0) return;
    const qty = parseFloat(newItemQty) || 0;
    const rate = parseFloat(newItemRate) || 0;
    const stock = parseFloat(newItemSelection.BalanceStock) || 0;

    // Validate stock before adding
    if (qty > stock) {
      showError(`Quantity (${qty}) exceeds available stock (${stock}) for ${newItemSelection.Name || 'this item'}`);
      return;
    }

    setProductionDetails(prev => [...prev, {
      ItemId: newItemSelection.Id,
      ItemName: newItemSelection.Name || '',
      ItemCode: newItemSelection.Code || '',
      Quantity: qty,
      Rate: rate,
      Amount: qty * rate,
      UnitQty: qty,
      BalanceStock: stock,
      IsActive: true,
      _isNew: true
    }]);
    setNewItemSelection(null);
    setNewItemQty('');
    setNewItemRate('');
    setAddItemKey(prev => prev + 1);
  };

  // Remove item from details
  const handleRemoveDetail = (index) => {
    setProductionDetails(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.ItemId) newErrors.ItemId = 'Product is required';
    if (!formData.VoucherDate) newErrors.VoucherDate = 'Date is required';
    if (productionDetails.length === 0) newErrors.Details = 'At least one production detail is required';

    // Stock validation
    const stockErrors = getStockErrors(productionDetails);
    if (stockErrors.length > 0) {
      showError(stockErrors.join(' | '));
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check stock for all production details — returns array of error strings
  const getStockErrors = (details) => {
    const stockErrors = [];
    details.forEach((detail, index) => {
      if (!detail.ItemId) return;
      const qty = parseFloat(detail.Quantity) || 0;
      if (qty <= 0) return;
      const itemData = allItems.find(i => i.Id === parseInt(detail.ItemId));
      const stock = detail.BalanceStock !== undefined
        ? parseFloat(detail.BalanceStock) || 0
        : parseFloat(itemData?.BalanceStock) || 0;
      if (qty > stock) {
        stockErrors.push(`Row ${index + 1} (${detail.ItemName || 'Item'}): qty (${qty}) exceeds available stock (${stock})`);
      }
    });
    return stockErrors;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        Production: {
          Id: mode === 'edit' ? (recordData?.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: branchId,
          ItemId: parseInt(formData.ItemId),
          ItemName: formData.ItemName,
          Quantity: parseFloat(totalQuantity) || 0,
          VoucherNumber: parseInt(formData.VoucherNumber) || 0,
          FinYear: formData.FinYear || null,
          VoucherDate: formData.VoucherDate,
          Narration: formData.Narration || null,
          IsActive: true,
          CreatedBy: mode === 'edit' ? (recordData?.CreatedBy || userId) : userId,
          CreatedDate: mode === 'edit' ? (recordData?.CreatedDate || null) : null,
          ModifiedBy: mode === 'edit' ? userId : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null
        },
        ProductionDetails: productionDetails.map(d => ({
          Id: d.Id || 0,
          ProductionId: mode === 'edit' ? (recordData?.Id || 0) : 0,
          ItemId: d.ItemId,
          Quantity: parseFloat(d.Quantity) || 0,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          IsActive: true,
          CreatedBy: userId,
          CreatedDate: null,
          ModifiedBy: mode === 'edit' ? userId : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null,
          PrevQuantity: d.PrevQuantity || 0
        }))
      };

      if (mode === 'edit') {
        await updateData('ProductionApi', recordData.Id, payload, token);
      logAudit(AUDIT_ACTION.UPDATE, 'ProductionApi', recordData.Id, { ...recordData }, { ...formData });
      } else {
        await postData('ProductionApi', '', payload, token);
      logAudit(AUDIT_ACTION.CREATE, 'ProductionApi', 0, null, { ItemId: payload?.Production?.ItemId || 0 });
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving production:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Production';
      case 'edit': return 'Edit Production';
      case 'view': return 'Production Details';
      default: return 'Production';
    }
  };

  const isReadOnly = mode === 'view';
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };

  // Totals
  const totalAmount = productionDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
  const totalQtySum = productionDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0);

  return (
  <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0' : '12px',
          background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
          padding: '2px',
          margin: isMobile ? '0' : '8px'
        }
      }}
    >
      <Box style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '10px',
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? '100vh' : 'auto',
        maxHeight: isMobile ? '100vh' : '85vh',
        overflow: 'hidden'
      }}>
        <DialogTitle style={{
          background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
          color: 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'auto'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FactoryIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
              {getTitle()}
            </Typography>
          </Box>
          <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={2}>
            {/* Product Selection */}
            <Grid item xs={12} sm={5}>
              <Autocomplete
                size="small"
                options={productItems}
                getOptionLabel={(option) => option.Code ? `${option.Code} - ${option.Name}` : option.Name || ''}
                isOptionEqualToValue={(option, value) => option.Id === value.Id}
                value={productItems.find(p => p.Id === parseInt(formData.ItemId)) || null}
                onChange={(_, newVal) => handleProductChange(newVal)}
                disabled={isReadOnly}
                renderOption={(props, option) => (
                  <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>
                    {option.Code ? `${option.Code} - ${option.Name}` : option.Name}
                  </li>
                )}
                ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Product *"
                    error={!!errors.ItemId}
                    helperText={errors.ItemId}
                    InputProps={{ ...params.InputProps, style: inputStyle }}
                    InputLabelProps={{ style: labelStyle }}
                  />
                )}
              />
            </Grid>

            {/* Production Quantity (multiplier) */}
            <Grid item xs={12} sm={2}>
              <TextField
                label="Produce Qty"
                type="number"
                value={totalQuantity}
                onChange={handleTotalQuantityChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="1"
                inputProps={{ min: 0.01, step: 1 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* Voucher Number */}
            <Grid item xs={6} sm={2}>
              <TextField
                label="Production No"
                value={formData.VoucherNumber ? `PDT/${formData.VoucherNumber}${formData.FinYear ? '/' + formData.FinYear : ''}` : ''}
                fullWidth
                size="small"
                disabled
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* Voucher Date */}
            <Grid item xs={6} sm={3}>
              <TextField
                label="Date *"
                type="date"
                value={formData.VoucherDate}
                onChange={(e) => setFormData(prev => ({ ...prev, VoucherDate: e.target.value }))}
                fullWidth
                size="small"
                disabled={isReadOnly}
                error={!!errors.VoucherDate}
                helperText={errors.VoucherDate}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle, shrink: true }}
              />
            </Grid>

            {/* Narration */}
            <Grid item xs={12}>
              <TextField
                label="Narration"
                value={formData.Narration}
                onChange={(e) => setFormData(prev => ({ ...prev, Narration: e.target.value }))}
                fullWidth
                size="small"
                disabled={isReadOnly}
                inputProps={{ maxLength: 50 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* Production Details (Child Items) */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '6px',
                marginTop: '4px',
                color: '#764ba2',
                fontSize: '13px'
              }}>
                Raw Materials
              </Typography>
              {errors.Details && (
                <Typography style={{ color: '#d32f2f', fontSize: '11px', marginBottom: '4px' }}>
                  {errors.Details}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              {detailsLoading ? (
                <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px', justifyContent: 'center' }}>
                  <CircularProgress size={16} />
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Loading raw materials...</span>
                </Box>
              ) : (
                <>
                {/* Add new item row */}
                {!isReadOnly && (
                  <Box style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <Autocomplete
                      key={addItemKey}
                      size="small"
                      options={availableItems}
                      getOptionLabel={(option) => option.Code ? `${option.Code} - ${option.Name}` : option.Name || ''}
                      isOptionEqualToValue={(option, value) => option.Id === value.Id}
                      value={newItemSelection}
                      onChange={(_, newVal) => setNewItemSelection(newVal)}
                      style={{ flex: 1 }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>
                          {option.Code ? `${option.Code} - ${option.Name}` : option.Name}
                        </li>
                      )}
                      ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Add Item"
                          size="small"
                          InputProps={{ ...params.InputProps, style: inputStyle }}
                          InputLabelProps={{ style: labelStyle }}
                        />
                      )}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Qty"
                      value={newItemQty}
                      onChange={(e) => setNewItemQty(e.target.value)}
                      style={{ width: '90px' }}
                      inputProps={{ min: 0.01, step: 0.01, style: { padding: '6px 8px', fontSize: '12px', textAlign: 'right' } }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Rate"
                      value={newItemRate}
                      onChange={(e) => setNewItemRate(e.target.value)}
                      style={{ width: '90px' }}
                      inputProps={{ min: 0, step: 0.01, style: { padding: '6px 8px', fontSize: '12px', textAlign: 'right' } }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                    <Tooltip title="Add Item">
                      <span>
                        <IconButton
                          onClick={handleAddNewItem}
                          disabled={!newItemSelection || !newItemQty || parseFloat(newItemQty) <= 0}
                          size="small"
                          style={{
                            background: newItemSelection && newItemQty && parseFloat(newItemQty) > 0
                              ? 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)'
                              : '#e0e0e0',
                            color: 'white',
                            borderRadius: '6px',
                            padding: '6px'
                          }}
                        >
                          <AddCircleOutlineIcon style={{ fontSize: '16px' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}

                <Box style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                  <Table size="small" style={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow style={{ background: '#f1f5f9' }}>
                        <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '6px 8px', width: '5%' }}>#</TableCell>
                        <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '6px 8px', width: isReadOnly ? '30%' : '25%' }}>Item Name</TableCell>
                        <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '6px 8px', width: '12%', textAlign: 'right' }}>Bal. Qty</TableCell>
                        <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '6px 8px', width: '18%', textAlign: 'right' }}>Quantity</TableCell>
                        <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '6px 8px', width: '18%', textAlign: 'right' }}>Rate</TableCell>
                        <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '6px 8px', width: '16%', textAlign: 'right' }}>Amount</TableCell>
                        {!isReadOnly && (
                          <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '6px 8px', width: '6%', textAlign: 'center' }}></TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productionDetails.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isReadOnly ? 6 : 7} style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '16px' }}>
                            {formData.ItemId ? 'No raw materials configured for this product' : 'Select a product to load raw materials'}
                          </TableCell>
                        </TableRow>
                      ) : productionDetails.map((detail, index) => (
                        <TableRow key={detail.ItemId || index} style={{ background: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                          <TableCell style={{ fontSize: '11px', padding: '5px 8px', color: '#555' }}>{index + 1}</TableCell>
                          <TableCell style={{ fontSize: '11px', padding: '5px 8px', fontWeight: '500' }}>
                            {detail.ItemCode ? `${detail.ItemCode} - ${detail.ItemName}` : detail.ItemName}
                          </TableCell>
                          <TableCell style={{ fontSize: '11px', padding: '5px 8px', textAlign: 'right', color: (parseFloat(detail.Quantity) || 0) > (parseFloat(detail.BalanceStock) || 0) ? '#e53935' : '#555' }}>
                            {parseFloat(detail.BalanceStock) || 0}
                          </TableCell>
                          <TableCell style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {isReadOnly ? (
                              <span style={{ fontSize: '11px' }}>{detail.Quantity}</span>
                            ) : (
                              <>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={detail.Quantity}
                                  onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)}
                                  inputProps={{ min: 0, step: 0.01, style: { padding: '3px 6px', fontSize: '11px', textAlign: 'right' } }}
                                  style={{ width: '100%' }}
                                  sx={{ 
                                    '& .MuiOutlinedInput-root': { 
                                      minHeight: '26px',
                                      ...(((parseFloat(detail.Quantity) || 0) > (parseFloat(detail.BalanceStock) || 0)) && {
                                        '& fieldset': { borderColor: '#e53935' }
                                      })
                                    } 
                                  }}
                                />
                                {(parseFloat(detail.Quantity) || 0) > (parseFloat(detail.BalanceStock) || 0) && (
                                  <Typography style={{ fontSize: '9px', color: '#e53935', textAlign: 'right', marginTop: '1px' }}>
                                    Stock: {detail.BalanceStock}
                                  </Typography>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {isReadOnly ? (
                              <span style={{ fontSize: '11px' }}>{detail.Rate}</span>
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                value={detail.Rate}
                                onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                                inputProps={{ min: 0, step: 0.01, style: { padding: '3px 6px', fontSize: '11px', textAlign: 'right' } }}
                                style={{ width: '100%' }}
                                sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' } }}
                              />
                            )}
                          </TableCell>
                          <TableCell style={{ fontSize: '11px', padding: '5px 8px', fontWeight: '600', color: '#1d2445', textAlign: 'right' }}>
                            {(parseFloat(detail.Amount) || 0).toFixed(2)}
                          </TableCell>
                          {!isReadOnly && (
                            <TableCell style={{ padding: '4px 8px', textAlign: 'center' }}>
                              <Tooltip title="Remove">
                                <IconButton size="small" onClick={() => handleRemoveDetail(index)} style={{ color: '#e53935', padding: '2px' }}>
                                  <DeleteOutlineIcon style={{ fontSize: '14px' }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      {productionDetails.length > 0 && (
                        <TableRow style={{ background: '#f1f5f9', borderTop: '2px solid #e2e8f0' }}>
                          <TableCell colSpan={3} style={{ fontSize: '11px', fontWeight: 700, padding: '6px 8px', textAlign: 'right' }}>
                            Total:
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', fontWeight: 700, padding: '6px 8px', color: '#1d2445', textAlign: 'right' }}>
                            {totalQtySum.toFixed(2)}
                          </TableCell>
                          <TableCell style={{ padding: '6px 8px' }}></TableCell>
                          <TableCell style={{ fontSize: '12px', fontWeight: 700, padding: '6px 8px', color: '#1d2445', textAlign: 'right' }}>
                            {totalAmount.toFixed(2)}
                          </TableCell>
                          {!isReadOnly && <TableCell style={{ padding: '6px 8px' }}></TableCell>}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{
          padding: '8px 12px',
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          gap: '6px',
          flexShrink: 0
        }}>
          <Button
            onClick={onClose}
            style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}
          >
            Cancel
          </Button>
          {!isReadOnly && formData.ItemId && (
            <Button
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              style={{
                background: 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)',
                color: 'white',
                textTransform: 'none',
                fontWeight: '600',
                borderRadius: '4px',
                padding: '4px 12px',
                fontSize: '12px'
              }}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Production' : 'Update Production'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    {/* Stock validation snackbar */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        sx={{ width: '100%', fontSize: '12px' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </>
  );
};

export default ProductionModal;
